import type {
  SidebarDimensions,
  SidebarAnimations,
  SidebarCollapseMode,
  SidebarHideOpensBehavior,
  SidebarVisualStyle,
} from "./Sidebar.types"
import {
  SidebarLayoutBehaviour,
} from "./Sidebar.types"
import {
  SIDEBAR_CSS_VARIABLES,
  DEFAULT_VISUAL_STYLE,
} from "./Sidebar.constants"

/**
 * Configuración para el generador de estilos
 * @interface
 */
interface StyleGeneratorConfig {
  dimensions: SidebarDimensions
  animations: SidebarAnimations
  collapseMode: SidebarCollapseMode
  hideOpensBehavior: SidebarHideOpensBehavior
  layoutBehaviour: SidebarLayoutBehaviour
  visualStyle: SidebarVisualStyle
}

/**
 * Genera los estilos CSS del sidebar usando CSS-in-JS y tokens de shadcn
 *
 * @description
 * Función que genera todo el CSS necesario para el sidebar,
 * utilizando los tokens CSS de shadcn definidos en globals.css:
 *
 * **Tokens utilizados:**
 * - `--sidebar`: Color de fondo
 * - `--sidebar-foreground`: Color de texto
 * - `--sidebar-primary`: Color primario (indicador, elementos activos)
 * - `--sidebar-accent`: Color de hover y estados secundarios
 * - `--sidebar-border`: Color de bordes
 * - `--sidebar-ring`: Color de focus ring (accesibilidad)
 *
 * Usa CSS Layers para organización y control de especificidad:
 * - sidebar-base: Variables y estilos base
 * - sidebar-states: Estados (:checked, :hover, etc.)
 * - sidebar-animations: Transiciones y animaciones
 *
 * @param {StyleGeneratorConfig} config - Configuración de dimensiones y animaciones
 * @returns {string} String con el CSS completo
 *
 * @example
 * ```tsx
 * const styles = generateSidebarStyles({
 *   dimensions: DEFAULT_DIMENSIONS,
 *   animations: DEFAULT_ANIMATIONS,
 *   collapseMode: SidebarCollapseMode.COLLAPSE,
 *   hideOpensBehavior: SidebarHideOpensBehavior.COLLAPSED
 * })
 *
 * <style jsx>{styles}</style>
 * ```
 *
 * @note
 * Los colores NO se configuran aquí, se obtienen automáticamente de los tokens
 * CSS de shadcn. Esto permite soporte automático para dark/light mode.
 */
export function generateSidebarStyles({
  dimensions,
  animations,
  collapseMode,
  hideOpensBehavior,
  layoutBehaviour,
  visualStyle,
}: StyleGeneratorConfig): string {
  // Calcular ancho cuando está cerrado según el modo
  const widthClosed =
    collapseMode === "hide" ? "0px" : `clamp(3rem, ${dimensions.collapsedWidth}, 6rem)`

  // Calcular ancho cuando está abierto según el modo y comportamiento
  const widthOpen =
    collapseMode === "hide" && hideOpensBehavior === "collapsed"
      ? `clamp(3rem, ${dimensions.collapsedWidth}, 6rem)`
      : `clamp(12rem, ${dimensions.expandedWidth}, 20rem)`

  // Determinar si es modo inline
  const isInline = layoutBehaviour === SidebarLayoutBehaviour.INLINE

  // Merge de visual style con defaults
  const mergedVisualStyle = {
    ...DEFAULT_VISUAL_STYLE,
    ...visualStyle,
  }

  return `
    @layer sidebar-base, sidebar-states, sidebar-animations, sidebar-liquid-glass;

    @layer sidebar-base {
      :root {
        ${SIDEBAR_CSS_VARIABLES.widthClosed}: ${widthClosed};
        ${SIDEBAR_CSS_VARIABLES.widthOpen}: ${widthOpen};
        ${SIDEBAR_CSS_VARIABLES.width}: var(${SIDEBAR_CSS_VARIABLES.widthClosed});
        ${SIDEBAR_CSS_VARIABLES.layoutBehaviour}: "${layoutBehaviour}";
        ${SIDEBAR_CSS_VARIABLES.isInline}: ${isInline ? '1' : '0'};
        ${SIDEBAR_CSS_VARIABLES.tooltipDistance}: ${dimensions.tooltipDistance || '1rem'};

        /* Variables de estilo visual */
        ${SIDEBAR_CSS_VARIABLES.marginTop}: ${mergedVisualStyle.marginTop};
        ${SIDEBAR_CSS_VARIABLES.marginBottom}: ${mergedVisualStyle.marginBottom};
        ${SIDEBAR_CSS_VARIABLES.marginLeft}: ${mergedVisualStyle.marginLeft};
        ${SIDEBAR_CSS_VARIABLES.marginRight}: ${mergedVisualStyle.marginRight};
        ${SIDEBAR_CSS_VARIABLES.sidebarHeight}: ${mergedVisualStyle.height};
        ${SIDEBAR_CSS_VARIABLES.sidebarBorderRadius}: ${mergedVisualStyle.borderRadius};
        ${SIDEBAR_CSS_VARIABLES.sidebarBoxShadow}: ${mergedVisualStyle.boxShadow};
      }

      .sidebar-wrapper {
        ${SIDEBAR_CSS_VARIABLES.background}: var(--sidebar);
        ${SIDEBAR_CSS_VARIABLES.borderColor}: var(--sidebar-border);
        ${SIDEBAR_CSS_VARIABLES.hover}: var(--sidebar-accent);
        ${SIDEBAR_CSS_VARIABLES.indicator}: var(--sidebar-primary);
        ${SIDEBAR_CSS_VARIABLES.indicatorHeight}: ${dimensions.indicatorHeight};
        container-type: inline-size;
        container-name: sidebar;
        /* Posicionamiento base */
        position: ${isInline ? 'relative' : 'absolute'};
        z-index: ${isInline ? 'auto' : '40'};
        left: ${isInline ? 'auto' : '0'};
        top: ${isInline ? 'auto' : '0'};
      }

      .sidebar-nav {
        width: min(var(${SIDEBAR_CSS_VARIABLES.width}), 100vw);
        /* background removed - handled by liquid-glass layer */
        border-color: var(${SIDEBAR_CSS_VARIABLES.borderColor});
        position: ${isInline ? 'relative' : 'absolute'};

        /* Aplicar variables de estilo visual */
        margin-top: var(--sidebar-margin-top);
        margin-bottom: var(--sidebar-margin-bottom);
        margin-left: var(--sidebar-margin-left);
        margin-right: var(--sidebar-margin-right);
        height: var(--sidebar-height);
        border-radius: var(--sidebar-border-radius);
        box-shadow: var(--sidebar-box-shadow);
      }
    }

    @layer sidebar-states {
      :root:has(#sidebar-toggle:checked) {
        ${SIDEBAR_CSS_VARIABLES.width}: var(${SIDEBAR_CSS_VARIABLES.widthOpen});
      }

      :where(.sidebar-wrapper:has(#sidebar-toggle:checked)) .toggle-icon {
        transform: rotate(180deg);
      }

      .sidebar-wrapper:has(#sidebar-toggle:checked) .toggle-button-wrapper {
        left: calc(var(${SIDEBAR_CSS_VARIABLES.widthOpen}) - 0.75rem);
      }

      .sidebar-wrapper:not(:has(#sidebar-toggle:checked)) .toggle-button-wrapper {
        left: ${collapseMode === "hide" ? "-0.75rem" : `calc(${dimensions.collapsedWidth} - 0.75rem)`};
      }
    }

    @layer sidebar-layout {
      /* Inline mode - desplaza contenido */
      body:has(.sidebar-wrapper[data-layout="inline"]) main {
        margin-left: var(--sidebar-width);
        transition: margin-left ${animations.widthTransitionDuration}ms ${animations.timingFunction};
      }

      /* Floating mode - sin desplazamiento (mantener comportamiento actual) */
      body:has(.sidebar-wrapper[data-layout="floating"]) main {
        margin-left: 0;
      }

      /* Responsive adjustments */
      @media (max-width: 768px) {
        body:has(.sidebar-wrapper[data-layout="inline"]) main {
          margin-left: 0;  /* En mobile, siempre floating */
        }

        .sidebar-wrapper[data-layout="inline"] {
          position: absolute;
          z-index: 40;
          left: 0;
          top: 0;
        }

        .sidebar-wrapper[data-layout="inline"] .sidebar-nav {
          position: absolute;
        }
      }
    }

    @layer sidebar-visual-responsive {
      /* Ajustes responsive para visual style */
      @media (max-width: 768px) {
        .sidebar-nav {
          /* En mobile, reducir márgenes para mejor uso de espacio */
          margin-top: calc(var(--sidebar-margin-top) * 0.5);
          margin-bottom: calc(var(--sidebar-margin-bottom) * 0.5);
          margin-left: calc(var(--sidebar-margin-left) * 0.5);
        }
      }

      /* Ajustes específicos para layout behaviour */
      body:has(.sidebar-wrapper[data-layout="inline"]) .sidebar-nav {
        /* En modo inline, mantener margins */
        margin-left: var(--sidebar-margin-left);
      }

      body:has(.sidebar-wrapper[data-layout="floating"]) .sidebar-nav {
        /* En modo floating, margin-left puede ser útil para separación visual */
        margin-left: var(--sidebar-margin-left);
      }

      /* Transiciones suaves para cambios de estilo visual */
      .sidebar-nav {
        transition:
          width ${animations.widthTransitionDuration}ms ${animations.timingFunction},
          margin-top ${animations.widthTransitionDuration}ms ${animations.timingFunction},
          margin-bottom ${animations.widthTransitionDuration}ms ${animations.timingFunction},
          margin-left ${animations.widthTransitionDuration}ms ${animations.timingFunction},
          margin-right ${animations.widthTransitionDuration}ms ${animations.timingFunction},
          height ${animations.widthTransitionDuration}ms ${animations.timingFunction},
          border-radius ${animations.widthTransitionDuration}ms ${animations.timingFunction},
          box-shadow ${animations.widthTransitionDuration}ms ${animations.timingFunction},
          overflow ${collapseMode === "hide" ? "hidden" : "visible"};
      }
    }

    @layer sidebar-liquid-glass {
      /* Liquid Glass V2 Styles - Solo cuando data attribute está presente */
      .sidebar-nav[data-liquid-glass="true"] {
        background: light-dark(
          hsl(0 0% 100% / var(--sidebar-glass, 0.05)),
          hsl(0 0% 0% / var(--sidebar-glass, 0.05))
        ) !important;
        backdrop-filter: blur(calc(var(--sidebar-glass-background, 8) * 1px)) url(#sidebar-liquid-glass) brightness(1.1) saturate(1.5) !important;
        -webkit-backdrop-filter: blur(calc(var(--sidebar-glass-background, 8) * 1px)) url(#sidebar-liquid-glass) brightness(1.1) saturate(1.5) !important;
        box-shadow:
          0 0 2px 1px light-dark(
            color-mix(in oklch, canvasText, #0000 85%),
            color-mix(in oklch, canvasText, #0000 65%)
          ) inset,
          0 0 10px 4px light-dark(
            color-mix(in oklch, canvasText, #0000 90%),
            color-mix(in oklch, canvasText, #0000 85%)
          ) inset,
          0px 4px 16px rgba(17, 17, 26, 0.05),
          0px 8px 24px rgba(17, 17, 26, 0.05),
          0px 16px 56px rgba(17, 17, 26, 0.05),
          0px 4px 16px rgba(17, 17, 26, 0.05) inset,
          0px 8px 24px rgba(17, 17, 26, 0.05) inset,
          0px 16px 56px rgba(17, 17, 26, 0.05) inset !important;

        /* Performance optimizations */
        will-change: backdrop-filter, transform;
        contain: layout style paint;
        transform: translateZ(0);
      }

      /* Asegurar que el background base no interfiera cuando está desactivado */
      .sidebar-nav[data-liquid-glass="false"] {
        background: var(--sidebar);
      }
    }

    @layer sidebar-animations {

      .toggle-icon {
        transition: transform ${animations.widthTransitionDuration}ms ${animations.timingFunction};
      }

      .toggle-button-wrapper {
        transition: left ${animations.widthTransitionDuration}ms ${animations.timingFunction};
      }

      .sidebar-indicator {
        transition: opacity ${animations.opacityTransitionDuration}ms ${animations.timingFunction};
      }
    }

    @container sidebar (max-width: 80px) {
      .sidebar-item-label {
        display: none;
      }
    }

    @supports selector(:has(*)) {
      .sidebar-wrapper:has(.sidebar-nav:hover) {
        ${SIDEBAR_CSS_VARIABLES.borderColor}: color-mix(in srgb, var(--sidebar-primary) 20%, var(--sidebar-border) 80%);
      }
    }

    /* Estilos para el hover/focus del icono elevado - CSS-first */
    .sidebar-item-icon-wrapper {
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    /* Elevación con keyboard focus (prioridad siempre) */
    .sidebar-item-icon-wrapper[data-focused="true"] {
      background: var(--sidebar-accent);
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
    }

    /* Elevación con mouse hover (solo si NO hay focus activo) - Mouse Dominance */
    .sidebar-nav:not(:has([data-focused="true"])) .sidebar-item-icon-wrapper[data-hovered="true"] {
      background: var(--sidebar-accent);
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
    }

    /* Asegurar que el icono SVG se escale correctamente */
    .sidebar-item-icon-wrapper svg {
      width: 1.5rem;
      height: 1.5rem;
    }
  `
}

/**
 * Genera estilos para el indicador visual usando tokens CSS
 *
 * @description
 * Función auxiliar para generar estilos dinámicos del indicador.
 * Útil para aplicar estilos inline con estado reactivo.
 *
 * Utiliza el token `--sidebar-primary` para el color del indicador,
 * asegurando consistencia con el theme del sidebar.
 *
 * @param {boolean} isVisible - Si el indicador está visible
 * @returns {React.CSSProperties} Objeto de estilos inline
 *
 * @example
 * ```tsx
 * const indicatorStyles = generateIndicatorStyles(isHovering && isOverItems)
 *
 * <div style={indicatorStyles} />
 * ```
 */
export function generateIndicatorStyles(isVisible: boolean): React.CSSProperties {
  return {
    background: "var(--sidebar-primary)",
    transform: "translateY(-50%)",
    opacity: isVisible ? 1 : 0,
    height: `var(${SIDEBAR_CSS_VARIABLES.indicatorHeight})`,
    width: "0.25rem",
  }
}
